import React from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Loader2, X, Save, Send, FileText } from 'lucide-react';

export function NewsletterCampaignDialog({ editing, setEditing, saving, onSave, segments, segmentPreview }) {
  return (
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="newsletter-campaign-dialog">
          <DialogHeader>
            <DialogTitle>{editing?._mode === 'create' ? 'Nouvelle campagne' : `Éditer : ${editing?.subject || ''}`}</DialogTitle>
            <DialogDescription>
              L'envoi se fera via Brevo. Le suivi des ouvertures/clics est automatique.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Sujet *</Label>
                <Input
                  value={editing.subject || ''}
                  onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                  data-testid="newsletter-campaign-field-subject"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Expéditeur (email)</Label>
                  <Input
                    placeholder="défaut : config Brevo"
                    value={editing.sender_email || ''}
                    onChange={(e) => setEditing({ ...editing, sender_email: e.target.value })}
                    data-testid="newsletter-campaign-field-sender-email"
                  />
                </div>
                <div>
                  <Label>Expéditeur (nom)</Label>
                  <Input
                    placeholder="O'SCOP Outremer"
                    value={editing.sender_name || ''}
                    onChange={(e) => setEditing({ ...editing, sender_name: e.target.value })}
                    data-testid="newsletter-campaign-field-sender-name"
                  />
                </div>
              </div>
              <div>
                <Label>Corps HTML * (éditeur WYSIWYG)</Label>
                <div className="bg-white border rounded" data-testid="newsletter-campaign-field-html">
                  <ReactQuill
                    theme="snow"
                    value={editing.html || ''}
                    onChange={(value) => setEditing({ ...editing, html: value })}
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'color': [] }, { 'background': [] }],
                        [{ 'align': [] }],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        ['blockquote'],
                        ['link', 'image'],
                        ['clean'],
                      ],
                    }}
                    style={{ minHeight: '250px' }}
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Astuce : utilisez la vue HTML brute ci-dessous pour le code source si besoin.
                </p>
              </div>
              <details>
                <summary className="text-xs text-slate-600 cursor-pointer">Source HTML (avancé)</summary>
                <Textarea
                  rows={6}
                  className="font-mono text-xs mt-2"
                  value={editing.html || ''}
                  onChange={(e) => setEditing({ ...editing, html: e.target.value })}
                  data-testid="newsletter-campaign-field-html-raw"
                />
              </details>
              <div>
                <Label>Texte brut (optionnel — fallback clients sans HTML)</Label>
                <Textarea
                  rows={4}
                  value={editing.plain_text || ''}
                  onChange={(e) => setEditing({ ...editing, plain_text: e.target.value })}
                  data-testid="newsletter-campaign-field-plain"
                />
              </div>

              {/* V38.06 — Ciblage / segmentation */}
              <div className="border-t pt-3 mt-2 space-y-2" data-testid="newsletter-campaign-targeting">
                <p className="text-sm font-semibold text-slate-800">Ciblage (laisser vide = tous les abonnés actifs)</p>
                <div className="grid md:grid-cols-3 gap-3">
                  <SegmentMultiSelect
                    label="Sources"
                    options={segments.sources}
                    values={editing.target_sources || []}
                    onChange={(v) => setEditing({ ...editing, target_sources: v })}
                    testid="newsletter-target-sources"
                  />
                  <SegmentMultiSelect
                    label="Territoires"
                    options={segments.territoires}
                    values={editing.target_territoires || []}
                    onChange={(v) => setEditing({ ...editing, target_territoires: v })}
                    testid="newsletter-target-territoires"
                  />
                  <SegmentMultiSelect
                    label="Tags"
                    options={segments.tags}
                    values={editing.target_tags || []}
                    onChange={(v) => setEditing({ ...editing, target_tags: v })}
                    testid="newsletter-target-tags"
                  />
                </div>
                <p className="text-xs text-emerald-700 font-semibold" data-testid="newsletter-segment-preview">
                  Aperçu : <b>{segmentPreview?.count ?? '—'}</b> abonné{(segmentPreview?.count || 0) > 1 ? 's' : ''} actif{(segmentPreview?.count || 0) > 1 ? 's' : ''} cibl{(segmentPreview?.count || 0) > 1 ? 'és' : 'é'}
                  {segmentPreview?.all ? ' (aucun filtre — tous les actifs)' : ''}
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="newsletter-campaign-dialog-cancel">
              Annuler
            </Button>
            <Button onClick={onSave} disabled={saving}
                    className="bg-emerald-700 hover:bg-emerald-800"
                    data-testid="newsletter-campaign-dialog-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
}

function SegmentMultiSelect({ label, options, values, onChange, testid }) {
  const [input, setInput] = React.useState('');
  const toggle = (v) => {
    if (values.includes(v)) {
      onChange(values.filter((x) => x !== v));
    } else {
      onChange([...values, v]);
    }
  };
  const addCustom = () => {
    const v = (input || '').trim();
    if (!v) return;
    if (!values.includes(v)) onChange([...values, v]);
    setInput('');
  };
  return (
    <div data-testid={testid}>
      <Label className="text-xs">{label}</Label>
      <div className="flex flex-wrap gap-1 mt-1 mb-1">
        {options.map((opt) => (
          <button
            type="button"
            key={opt}
            onClick={() => toggle(opt)}
            className={`px-2 py-0.5 text-[11px] rounded-full border ${values.includes(opt) ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200'}`}
            data-testid={`${testid}-option-${opt}`}
          >
            {opt}
          </button>
        ))}
      </div>
      {values.filter((v) => !options.includes(v)).length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1">
          {values.filter((v) => !options.includes(v)).map((v) => (
            <span key={v} className="px-2 py-0.5 text-[11px] rounded-full bg-amber-200 text-amber-900 border border-amber-300 inline-flex items-center gap-1">
              {v}
              <button type="button" onClick={() => toggle(v)} className="ml-1 hover:text-red-700">×</button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-1">
        <Input
          placeholder="+ ajouter"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
          className="text-xs h-7"
        />
      </div>
    </div>
  );
}

